import { useMemo, useState } from 'react';
// The document itself, inlined at build time. There is no second copy and
// no endpoint: what the view shows is the file in the repo, and editing
// docs/brand-calendar.md is how the view changes.
import markdown from '../../../../docs/brand-calendar.md?raw';
import { BRAND_META, BRAND_ORDER } from '../brand';
import { parseCalendarDoc, type CalendarDocEntry } from '../calendar-md';
import type { BrandName, Me } from '../types';

/**
 * The full brand calendar, as written.
 *
 * The "Coming up" panel on the Board shows the actionable subset - the
 * dates with a lead time that put a card on the board. This is the
 * reference behind it: every date worth knowing about, which is far more
 * than anything should be firing on. Keeping them separate is what stops
 * ninety days of panel holding a hundred entries, and this view is the
 * answer to "where did the rest of it go".
 */
export function CalendarView({ me }: { me: Me }) {
  const months = useMemo(() => parseCalendarDoc(markdown), []);
  const thisMonth = new Date().toISOString().slice(0, 7);

  const [brand, setBrand] = useState<BrandName | 'all'>('all');
  const [query, setQuery] = useState('');
  // Past months are collapsed rather than absent: a calendar you cannot
  // look backwards in is a to-do list.
  const [showEarlier, setShowEarlier] = useState(false);

  // An operator sees their own brands, as everywhere else in the Shell.
  const visibleBrands = BRAND_ORDER.filter((b) => me.brands.includes(b));

  const matches = (e: CalendarDocEntry): boolean => {
    if (brand !== 'all' && !e.brands.includes(brand)) return false;
    // An entry tagged for no brand at all is house-wide, not hidden.
    if (e.brands.length > 0 && !e.brands.some((b) => me.brands.includes(b))) return false;
    if (query.trim() === '') return true;
    const q = query.toLowerCase();
    return e.text.toLowerCase().includes(q) || e.date.toLowerCase().includes(q);
  };

  const shown = months
    .filter((m) => showEarlier || m.key >= thisMonth)
    .map((m) => ({ ...m, entries: m.entries.filter(matches) }))
    .filter((m) => m.entries.length > 0);

  const total = shown.reduce((n, m) => n + m.entries.length, 0);

  return (
    <section className="calendar-view">
      <div className="card">
        <div className="card-head">
          <span className="card-title">Brand calendar</span>
          <span className="card-note">{total} dates</span>
        </div>
        <p className="muted">
          The full reference. The Board's "Coming up" panel shows the smaller set that carries a
          lead time and can become a card; this is everything, including the dates nobody has
          decided to act on yet.
        </p>

        <div className="calendar-filters">
          <div className="board-picker" role="tablist" aria-label="Brand">
            <button
              role="tab"
              aria-selected={brand === 'all'}
              className="brand-tab"
              style={{ ['--tab-accent' as string]: 'var(--ink)' }}
              onClick={() => setBrand('all')}
            >
              All brands
            </button>
            {visibleBrands.map((b) => (
              <button
                key={b}
                role="tab"
                aria-selected={brand === b}
                className="brand-tab"
                style={{ ['--tab-accent' as string]: BRAND_META[b].series }}
                onClick={() => setBrand(b)}
              >
                {BRAND_META[b].label}
              </button>
            ))}
          </div>
          <input
            className="calendar-search"
            value={query}
            placeholder="Search the calendar"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the calendar"
          />
          <button type="button" className="ghost" onClick={() => setShowEarlier((v) => !v)}>
            {showEarlier ? 'Hide earlier months' : 'Show earlier months'}
          </button>
        </div>
      </div>

      {shown.length === 0 && (
        <div className="card">
          <p className="muted">Nothing matches. Clear the search or choose another brand.</p>
        </div>
      )}

      {shown.map((m) => (
        <div className="card" key={m.key}>
          <div className="card-head">
            <span className="card-title">{m.label}</span>
            {m.key === thisMonth && <span className="card-note">this month</span>}
          </div>
          <ul className="calendar-list">
            {m.entries.map((e, i) => (
              <li key={`${m.key}-${i}`} className="calendar-row">
                <span className="calendar-dots" aria-hidden>
                  {e.brands.map((b) => (
                    <i key={b} style={{ background: BRAND_META[b]?.series ?? 'var(--ink)' }} />
                  ))}
                </span>
                <span className="calendar-main">
                  <span className="calendar-name">
                    {e.date && <b className="calendar-date">{e.date}</b>}
                    {e.text}
                  </span>
                  <span className="calendar-meta">
                    {/* Named, never colour alone. */}
                    {e.brands.map((b) => BRAND_META[b]?.label ?? b).join(', ') || 'House-wide'}
                    {e.tbc && ' · date not confirmed'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
